import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {isCoreObject, isCoreObjectWithInterface} from 'app-core/framework/CoreObjectIntrospectionUtils';
import CoreObjectStarIconRenderer, {
  StarredItemListingExtras,
} from 'app-core/platform/starrable/CoreObjectStarIcon';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as biojsUtil from 'biojs/Util';
import {ChemSearchFolderItemListingExtras, MOLECULE_ROW_HEIGHT} from 'chemistry/ChemistryConstants';
import {shouldShowChemSearchColumnsFirst} from 'chemistry/ChemSearchUtils';
import MoleculeChemicalStructure, {
  MoleculeChemicalStructureSize,
} from 'chemistry/molecules/tabs/MoleculeChemicalStructure';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {FolderSection, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants, {QUICK_EDIT_FOLDER_ITEMS} from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {FinderResult} from 'constants/NavConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
// @ts-expect-error
import FileMenu from 'file-browser/FileMenu';
import FinderUtil from 'file-browser/FinderUtil';
import {BioentitySummary, FileSummary} from 'folder-items/FileConstants';
import * as FileUtil from 'folder-items/FileUtil';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ExpandedItemTableQuery} from 'nav/expanded/__generated/ExpandedItemTableQuery';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import MixtureIngredientsList from 'nav/expanded/MixtureIngredientsList';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import {EntrySummary} from 'notebook/ELNConstants';
import QuickEditModal from 'notebook/notes/QuickEditModal';
import ReviewLifecycleNamesLabel, {
  ReviewLifecycleNames,
} from 'notebook/reviews/review-lifecycles/ReviewLifecycleNamesLabel';
import {getReviewLifecycleTypeText} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {ReviewChangeTimestamps} from 'notebook/reviews/ReviewConstants';
import ReviewStatus from 'notebook/reviews/ReviewStatus';
import notifications from 'notifications';
import * as RegistryIdentifierUtilRest from 'registry/RegistryIdentifierUtilRest';
import BioentityValidationStatus from 'registry/validation/BioentityValidationStatus';
import getValidationErrorText from 'schemas/getValidationErrorText';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {
  BIT_SCORE_ATTRIBUTE,
  E_VALUE_ATTRIBUTE,
  HIT_LENGTH_ATTRIBUTE,
  PERCENT_IDENTITY_ATTRIBUTE,
  QUERY_COVER_ATTRIBUTE,
} from 'search/blast/BLASTConstants';
import {ListableItem, ListableItemExtras, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import {getChipsRowHeight} from 'ui-library/chips/ChipList';
import ConnectedAuthors from 'ui-library/ConnectedAuthors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import * as modals from 'util/Modals';
import {BasicEvent} from 'util/Types';
import {maybeAddWorklistPositionColumn} from 'worklists/sidepanel-components/WorklistItemsSidepanel';

import {DatasetListingGraphQL} from '../../../graphql-fragments/data-frames/__generated/DatasetListingGraphQL';
import BaseListingRendererWithPlugins from './BaseListingRendererWithPlugins';
import ContainableInventorySummary, {
  buildSampleSearchLink,
  ContainableInventoryInfo,
} from './ContainableInventorySummary';

type ExpandedItemTableMixtureGraphQL = ExtractByTypename<
  ExpandedItemTableQuery['bioentities'][number],
  'MixtureNode'
>;

type ListableFolderItem = ListingTypes[ListingRendererType.FOLDER_ITEM];

interface BioentityAliasInfoByBioentityId {
  [bioentityId: string]: Array<BioentityAliasInfo> | undefined;
}

export interface FolderItemListingExtras extends StarredItemListingExtras, StudyableItemListingExtras {
  reviewChangeTimestampsByEntryId?: {[entryId: string]: ReviewChangeTimestamps | undefined};
  validationExtrasByBioentityId?: {[bioentityId: string]: ValidationExtras | undefined};
  inventoryInfoByContainableId?: {[bioentityId: string]: ContainableInventoryInfo} | 'LOADING';
  shouldShowShortcuts?: boolean;
  bioentityAliasInfosByBioentityId?: BioentityAliasInfoByBioentityId;
  isRegistry?: boolean;
  collection?: string | null;
  section?: FolderSection | null;
  mixturesById?: {[mixtureId: string]: ExpandedItemTableMixtureGraphQL};
  chemSearchFolderItemListingExtras?: ChemSearchFolderItemListingExtras | null;
  searchSection?: NavConstants.Section;
  itemAttributes?: Array<string>;
}

export type FolderItemColumnExtras = Pick<
  FolderItemListingExtras,
  'bioentityAliasInfosByBioentityId' | 'isRegistry' | 'collection' | 'section'
>;

function getBioentityAliasInfos(
  result: ListableFolderItem,
  bioentityAliasInfosByBioentityId: BioentityAliasInfoByBioentityId | undefined
): Array<BioentityAliasInfo> {
  if (!bioentityAliasInfosByBioentityId) {
    return [];
  }
  return bioentityAliasInfosByBioentityId[result.id] || [];
}

// Get registry identifier for file (ex: "pBN001"), or null if no collection listed.
function getRegistryIdentifier(
  folderItem: ListableFolderItem,
  bioentityAliasInfosByBioentityId: BioentityAliasInfoByBioentityId | undefined,
  isRegistry: boolean | undefined,
  collection?: string | null
): string | null {
  const aliasInfos = getBioentityAliasInfos(folderItem, bioentityAliasInfosByBioentityId);
  return isRegistry ? RegistryIdentifierUtilRest.getRegistryIdentifier(aliasInfos, collection) : null;
}

function getOtherAliases(
  folderItem: ListableFolderItem,
  bioentityAliasInfosByBioentityId: BioentityAliasInfoByBioentityId | undefined,
  isRegistry: boolean | undefined,
  collection?: string | null
): Array<string> {
  // eslint-disable-next-line no-restricted-syntax
  return _.without(
    // eslint-disable-next-line no-restricted-syntax
    _.pluck(getBioentityAliasInfos(folderItem, bioentityAliasInfosByBioentityId), 'alias'),
    getRegistryIdentifier(folderItem, bioentityAliasInfosByBioentityId, isRegistry, collection),
    folderItem.name
  );
}

function _getAliasesText(aliases: Array<string>): string {
  // eslint-disable-next-line no-restricted-syntax
  return aliases.sort().join(', ');
}

function maybeAddChemicalStructureColumn(
  columns: Array<ColumnConfiguration<ListableFolderItem>>,
  items: Array<ListableFolderItem>,
  chemSearchFolderItemListingExtras?: ChemSearchFolderItemListingExtras | null
): void {
  const shouldShowChemicalStructureColumn = items.some((item) => APIUtil.isMolecule(item.id));
  if (shouldShowChemicalStructureColumn) {
    const chemicalStructureColumnDef: ColumnConfiguration<ListableFolderItem> = {
      column: {
        header: 'Chemical Structure',
        accessorKey: ColumnAccessorKey.CHEMICAL_STRUCTURE,
        // Setting this to 400 is arbitrary based on what looked reasonable in the table.
        width: 400,
      },
      render: (folderItem: ListableFolderItem) => (
        <div className={css(styles.flex)}>
          <MoleculeChemicalStructure
            key={folderItem.id}
            moleculeId={folderItem.id}
            substructureQuery={chemSearchFolderItemListingExtras?.substructureQuery}
            shouldHighlightSubstructure={chemSearchFolderItemListingExtras?.shouldHighlightSubstructure}
            size={MoleculeChemicalStructureSize.ITEM_LISTING_SIZE}
          />
        </div>
      ),
      isHidden: (folderItem: ListableFolderItem) => !APIUtil.isMolecule(folderItem.id),
    };
    if (shouldShowChemSearchColumnsFirst(items)) {
      columns.splice(1, 0, chemicalStructureColumnDef);
    } else {
      columns.push(chemicalStructureColumnDef);
    }
  }
}

function maybeAddChemicalSimilarityColumn(
  columns: Array<ColumnConfiguration<ListableFolderItem>>,
  items: Array<ListableFolderItem>,
  chemSearchFolderItemListingExtras?: ChemSearchFolderItemListingExtras | null
): void {
  const shouldShowColumn =
    chemSearchFolderItemListingExtras?.searchScoresById &&
    Object.values(chemSearchFolderItemListingExtras.searchScoresById).some((score) => score != null) &&
    items.some((item) => APIUtil.isMolecule(item.id));
  if (shouldShowColumn) {
    const chemicalSimilarityColumnDef: ColumnConfiguration<ListableFolderItem> = {
      column: {
        header: getColumnHeaderFromSortKey(SortKey.SORT_CHEMICAL_SIMILARITY),
        accessorKey: ColumnAccessorKey.CHEMICAL_SIMILARITY,
        width: 180,
        isSortable: true,
        sortKey: SortKey.SORT_CHEMICAL_SIMILARITY,
      },
      render: (folderItem: ListableFolderItem) =>
        chemSearchFolderItemListingExtras?.searchScoresById?.[folderItem.id]?.toFixed(4),
      isHidden: (folderItem: ListableFolderItem) => !APIUtil.isMolecule(folderItem.id),
    };
    if (shouldShowChemSearchColumnsFirst(items)) {
      columns.splice(2, 0, chemicalSimilarityColumnDef);
    } else {
      columns.push(chemicalSimilarityColumnDef);
    }
  }
}

export function makeReviewProcessesColumn<T>(opts: {
  isApplicable: (item: T) => boolean;
  // Prefer getLifecycleNames when the names are already loaded (e.g. via the listing query); it renders
  // them directly. Fall back to getLifecycleIds, which resolves the names with a per-row query.
  getLifecycleNames?: (item: T) => Array<string> | undefined;
  getLifecycleIds?: (item: T) => Array<string> | undefined;
}): ColumnConfiguration<T> {
  return {
    column: {
      header: 'Review Processes',
      accessorKey: ColumnAccessorKey.REVIEW_LIFECYCLES,
      flexGrow: 1,
      isCollapsible: true,
      isSortable: false,
    },
    render: (item: T) => {
      if (!opts.isApplicable(item)) {
        return null;
      }
      const names = opts.getLifecycleNames?.(item);
      if (names) {
        return <ReviewLifecycleNames names={names} />;
      }
      const ids = opts.getLifecycleIds?.(item);
      return ids ? <ReviewLifecycleNamesLabel lifecycleIds={ids} /> : null;
    },
    isHidden: (item: T) => !opts.isApplicable(item),
  };
}

function getReviewLifecycleColumns(): Array<ColumnConfiguration<ListableFolderItem>> {
  return [
    {
      column: {
        header: 'Review Process',
        accessorKey: ColumnAccessorKey.REVIEW_LIFECYCLE,
        flexGrow: 1,
        isCollapsible: true,
        isSortable: false,
      },
      render: (entrySummary: EntrySummary) =>
        APIUtil.isBaseEntry(entrySummary.id) && (
          <span className="u-ellipsis-text">{entrySummary.review?.reviewLifecycleVersion?.name}</span>
        ),
      isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
    },
    makeReviewProcessesColumn<ListableFolderItem>({
      getLifecycleIds: (folderItem) => (folderItem as StringIDFolder).source?.reviewLifecyclesIds,
      isApplicable: (folderItem) => APIUtil.isFolder(folderItem.id),
    }),
    {
      column: {
        header: 'Review Process Type',
        accessorKey: ColumnAccessorKey.REVIEW_LIFECYCLE_TYPE,
        flexGrow: 1,
        isCollapsible: true,
        isSortable: false,
      },
      render: (entrySummary: EntrySummary) =>
        APIUtil.isBaseEntry(entrySummary.id) && (
          <span className="u-ellipsis-text">
            {getReviewLifecycleTypeText(entrySummary.review?.reviewLifecycleVersion?.lifecycleType)}
          </span>
        ),
      isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
    },
  ];
}

function getBLASTColumns(itemAttributes?: Array<string>): Array<ColumnConfiguration<ListableFolderItem>> {
  if (!BenchlingConfig.ENABLE_BLAST_SEARCH || !itemAttributes) {
    return [];
  }

  return compact([
    itemAttributes.includes(E_VALUE_ATTRIBUTE)
      ? {
          column: {
            header: getColumnHeaderFromSortKey(SortKey.SORT_BLAST_E_VALUE),
            accessorKey: ColumnAccessorKey.E_VALUE,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: true,
            sortKey: SortKey.SORT_BLAST_E_VALUE,
          },
          render: (folderItem: ListableFolderItem & Partial<ListableItemExtras>) =>
            folderItem.attributeValues?.[E_VALUE_ATTRIBUTE] && (
              <span className="u-ellipsis-text">
                {folderItem.attributeValues[E_VALUE_ATTRIBUTE].toExponential(2)}
              </span>
            ),
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isNucleotideSequence(folderItem.id) && !APIUtil.isProtein(folderItem.id),
        }
      : null,
    itemAttributes.includes(PERCENT_IDENTITY_ATTRIBUTE)
      ? {
          column: {
            header: 'Percent Identity',
            accessorKey: ColumnAccessorKey.PERCENT_IDENTITY,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem & Partial<ListableItemExtras>) =>
            folderItem.attributeValues?.[PERCENT_IDENTITY_ATTRIBUTE] && (
              <span className="u-ellipsis-text">
                {(100 * folderItem.attributeValues[PERCENT_IDENTITY_ATTRIBUTE]).toFixed(2)}%
              </span>
            ),
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isNucleotideSequence(folderItem.id) && !APIUtil.isProtein(folderItem.id),
        }
      : null,
    itemAttributes.includes(QUERY_COVER_ATTRIBUTE)
      ? {
          column: {
            header: 'Query Cover',
            accessorKey: ColumnAccessorKey.QUERY_COVER,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem & Partial<ListableItemExtras>) =>
            folderItem.attributeValues?.[QUERY_COVER_ATTRIBUTE] && (
              <span className="u-ellipsis-text">
                {(100 * folderItem.attributeValues[QUERY_COVER_ATTRIBUTE]).toFixed(2)}%
              </span>
            ),
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isNucleotideSequence(folderItem.id) && !APIUtil.isProtein(folderItem.id),
        }
      : null,
    itemAttributes.includes(BIT_SCORE_ATTRIBUTE)
      ? {
          column: {
            header: 'Bit Score',
            accessorKey: ColumnAccessorKey.BIT_SCORE,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem & Partial<ListableItemExtras>) =>
            folderItem.attributeValues?.[BIT_SCORE_ATTRIBUTE] && (
              <span className="u-ellipsis-text">{folderItem.attributeValues[BIT_SCORE_ATTRIBUTE]}</span>
            ),
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isNucleotideSequence(folderItem.id) && !APIUtil.isProtein(folderItem.id),
        }
      : null,
    itemAttributes.includes(HIT_LENGTH_ATTRIBUTE)
      ? {
          column: {
            header: 'Length',
            accessorKey: ColumnAccessorKey.HIT_LENGTH,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem & Partial<ListableItemExtras>) =>
            folderItem.attributeValues?.[HIT_LENGTH_ATTRIBUTE] && (
              <span className="u-ellipsis-text">{folderItem.attributeValues[HIT_LENGTH_ATTRIBUTE]}</span>
            ),
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isNucleotideSequence(folderItem.id) && !APIUtil.isProtein(folderItem.id),
        }
      : null,
  ]);
}

export function getIdColumnRender(
  item: ListableFolderItem,
  bioentityAliasInfosByBioentityId?: BioentityAliasInfoByBioentityId,
  isRegistry?: boolean,
  collection?: string | null
): React.ReactNode {
  const registryIdentifier = getRegistryIdentifier(
    item,
    bioentityAliasInfosByBioentityId,
    isRegistry,
    collection
  );
  return registryIdentifier ? (
    <span className="u-flex-display">
      <span className="u-tiny-padding-right">
        <Tooltip title={registryIdentifier} container="body" placement={TooltipPlacement.TOP} delay={350}>
          <span className="u-ellipsis-text">{registryIdentifier}</span>
        </Tooltip>
      </span>
      <BioentityValidationStatus bioentity={item as BioentitySummary} />
    </span>
  ) : (
    <span className="u-faded">None</span>
  );
}

export function getNameColumnRender(
  item: ListableFolderItem,
  bioentityAliasInfosByBioentityId?: BioentityAliasInfoByBioentityId,
  isRegistry?: boolean,
  collection?: string | null
): React.ReactNode {
  const bioentityAliasInfos = getBioentityAliasInfos(item, bioentityAliasInfosByBioentityId);
  // Show colored name and aliases if the file has aliases and this is not a registry (since there we
  // show aliases in separate columns). In other words, show only the name if either the file doesn't
  // have aliases or this is the registry.
  const shouldShowAliasesInNameCell = isRegistry || _.isEmpty(bioentityAliasInfos);
  // Similarly, if this is a registry the validation status is in another column so we don't need it
  // here. Show validation status in name cell if the feature flag is turned on for this deploy,
  // item is a bioentity, and this is not a registry.
  const shouldShowValidationStatus = APIUtil.isBiologicalEntity(item.id) && !isRegistry;
  return (
    <span className="u-flex-display">
      <span className="u-tiny-padding-right">
        <Tooltip title={item.name} container="body" placement={TooltipPlacement.TOP} delay={350}>
          <span className="u-ellipsis-text">
            {shouldShowAliasesInNameCell
              ? item.name
              : RegistryIdentifierUtilRest.getAliasComponents(bioentityAliasInfos, item.name, collection)}
            <ArchiveRecordLabel item={item} />
          </span>
        </Tooltip>
      </span>
      {shouldShowValidationStatus && <BioentityValidationStatus bioentity={item as BioentitySummary} />}
    </span>
  );
}

export default class FolderItemListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.FOLDER_ITEM> {
  listingRendererType = ListingRendererType.FOLDER_ITEM;

  renderIcon(
    folderItem: ListableFolderItem,
    {shouldShowShortcuts}: {shouldShowShortcuts: boolean}
  ): JSX.Element {
    return shouldShowShortcuts && FileUtil.isInRegistry(folderItem) ? (
      <span>
        <BenchlingIcon
          className="scrollableTable-cell-icon scrollableTable-cell--shortcutFileIcon"
          icon={FileBrowserUtil.getFileIconName(folderItem)}
        />
        <BenchlingIcon
          className="scrollableTable-cell--shortcutRegistryIcon"
          icon={ModelTypeConstants.REGISTRY}
        />
      </span>
    ) : (
      <BenchlingIcon
        className="scrollableTable-cell-icon"
        icon={FileBrowserUtil.getFileIconName(folderItem)}
      />
    );
  }

  _hasQuickEdit = (folderItem: ListableFolderItem & ListableItem): boolean =>
    APIUtil.isFolderContainable(folderItem.id) &&
    folderItem.permissions.writable &&
    !isCoreObject(folderItem);

  _handleQuickEdit = async (folderItem: ListableFolderItem): Promise<void> => {
    if (!QUICK_EDIT_FOLDER_ITEMS.includes(APIUtil.apiIdToType(folderItem.id))) {
      modals
        .prompt('Edit Name', folderItem.name, (name) => name !== '' && name !== folderItem.name)
        .done((newName) => {
          FileBrowserActions.renameFileWithDataMods(folderItem.id, FileUtil.isInRegistry(folderItem), {
            oldName: folderItem.name,
            newName: newName!,
            parentFolderId: FinderUtil.parentFolderId(folderItem as FinderResult)!,
            notifyOnSuccess: true,
          }).catch((response) => {
            notifications.notifyError(response);
          });
        });
    } else {
      await legacyRunModal(QuickEditModal, {
        fileId: folderItem.id,
        initialName: folderItem.name,
        parentFolderId: FinderUtil.parentFolderId(folderItem as FinderResult),
        isRegisteredItem: FileUtil.isInRegistry(folderItem),
      });
    }
  };

  openContextMenu(
    folderItem: ListableFolderItem & ListableItem,
    e: BasicEvent,
    extras: {finderId: string; isListing: boolean}
  ): void {
    FileMenu.open(e, {
      file: folderItem,
      finderId: extras.finderId,
      isListing: extras.isListing,
      handleQuickEdit: this._hasQuickEdit(folderItem) ? this._handleQuickEdit : null,
    });
  }

  getNonPluginColumnConfigurations(
    folderItems: Array<ListableFolderItem>,
    {
      bioentityAliasInfosByBioentityId,
      reviewChangeTimestampsByEntryId,
      validationExtrasByBioentityId,
      inventoryInfoByContainableId,
      mixturesById,
      isRegistry,
      section,
      collection,
      chemSearchFolderItemListingExtras,
      searchSection,
      itemAttributes,
      starredItemsByObjectId,
      itemStudyAttributes,
    }: FolderItemListingExtras
  ): Array<ColumnConfiguration<ListableFolderItem>> {
    const isNotebookSection = section === FileBrowserConstants.NOTEBOOK_SECTION;
    const isAllSection = section === FileBrowserConstants.ALL_SECTION;
    const resultsInCollection = isRegistry
      ? folderItems.filter((result) => {
          for (const {registryCollectionOwner} of getBioentityAliasInfos(
            result,
            bioentityAliasInfosByBioentityId
          )) {
            if (registryCollectionOwner && registryCollectionOwner.id === collection) {
              return true;
            }
          }
          return false;
        })
      : folderItems;
    // If registry, show name column only if there are files that have names, distinct from their registry
    // identifier. If not registry, always show name column.
    const shouldShowNameColumn =
      !isRegistry ||
      resultsInCollection.some(
        (result) =>
          getRegistryIdentifier(result, bioentityAliasInfosByBioentityId, isRegistry, collection) !==
          result.name
      );
    // Show other aliases column if registry and there are files that have other aliases, distinct from
    // their registry identifier and name.
    const shouldShowOtherAliasesColumn =
      isRegistry &&
      resultsInCollection.some(
        (result) =>
          !_.isEmpty(getOtherAliases(result, bioentityAliasInfosByBioentityId, isRegistry, collection))
      );
    const shouldShowInventoryColumn =
      BenchlingConfig.INVENTORY_ENABLE_SUMMARY_ON_ENTITY_SEARCH &&
      isFeatureEnabled({feature: InventoryFeature.INVENTORY_SHOW_DETAILS}) &&
      folderItems.some((folderItem) => APIUtil.isBiologicalEntity(folderItem.id)) &&
      // We don't want to display an inventory link outside of a sidepanel for a couple of reasons:
      //  - The summary component currently depends on reading the sidepanel state to identify selected
      //    items, which will fail when no sidepanel is open, and return incorrect items when one is if
      //    executing in a non-sidepanel context.
      //  - The inventory link involves triggering sidepanel navigation when clicked, which we don't want
      //    to do when in a context like a modal that would cause background navigation actions and/or
      //    closing the modal and disrupting the user's flow.
      // To that end, confirm that the search section is the active sidepanel section (if any).
      searchSection === SidepanelStore.getSection();

    // mixturesById is fetched via GQL, so to circumvent that we use whether the folderItems contain mixtures
    // as a proxy for determining if the mixtures columns should be shown. However, the column's cells
    // will not be populated if mixturesById is undefined.
    const shouldShowMixturesColumn = folderItems.some((item) => APIUtil.isMixture(item.id));

    let columns: Array<ColumnConfiguration<ListableFolderItem>> = _.compact([
      isRegistry &&
        ({
          column: {
            header: getColumnHeaderFromSortKey(SortKey.SORT_REGISTRY_IDENTIFIER_KEY),
            accessorKey: ColumnAccessorKey.ID,
            isCollapsible: false,
            isSortable: true,
            sortKey: SortKey.SORT_REGISTRY_IDENTIFIER_KEY,
            width: 200,
          },
          render: (folderItem: ListableFolderItem) =>
            getIdColumnRender(folderItem, bioentityAliasInfosByBioentityId, isRegistry, collection),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      shouldShowNameColumn &&
        ({
          column: {
            header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
            accessorKey: ColumnAccessorKey.NAME,
            isCollapsible: false,
            isSortable: true,
            sortKey: SortKey.SORT_NAME_KEY,
          },
          render: (item: ListableFolderItem) =>
            getNameColumnRender(item, bioentityAliasInfosByBioentityId, isRegistry, collection),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      {
        column: {
          accessorKey: ColumnAccessorKey.OWNER,
          header: 'Owner',
          flexGrow: 1,
          width: 200,
          isCollapsible: false,
        },
        render: (folderItem: StringIDFolder) => <div>{folderItem.owner.name}</div>,
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.isFolder(folderItem.id),
      },
      ...getBLASTColumns(itemAttributes),
      shouldShowInventoryColumn &&
        ({
          column: {
            header: 'Inventory',
            accessorKey: ColumnAccessorKey.INVENTORY,
            isCollapsible: true,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem) =>
            inventoryInfoByContainableId === 'LOADING' ? (
              <HorizontalThrobber />
            ) : (
              <ContainableInventorySummary
                containableInventoryInfo={inventoryInfoByContainableId?.[folderItem.id]}
                sampleSearchLink={
                  BenchlingConfig.ENABLE_SIGHTGLASS_V3_GROUP_BY
                    ? buildSampleSearchLink(folderItem.id, inventoryInfoByContainableId)
                    : null
                }
              />
            ),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      shouldShowOtherAliasesColumn &&
        ({
          column: {
            header: 'Aliases',
            accessorKey: ColumnAccessorKey.ALIASES,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem) => (
            <Tooltip
              title={_getAliasesText(
                // eslint-disable-next-line no-restricted-syntax
                _.pluck(getBioentityAliasInfos(folderItem, bioentityAliasInfosByBioentityId), 'alias')
              )}
              container="body"
              placement={TooltipPlacement.TOP}
              delay={350}
            >
              <span className="u-ellipsis-text">
                {_getAliasesText(
                  getOtherAliases(folderItem, bioentityAliasInfosByBioentityId, isRegistry, collection)
                )}
              </span>
            </Tooltip>
          ),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      !isRegistry &&
        ({
          column: {
            header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
            accessorKey: ColumnAccessorKey.ID,
            isCollapsible: false,
            isSortable: true,
            sortKey: SortKey.SORT_IDENTIFIER_KEY,
          },
          render: (folderItem: ListableFolderItem) => {
            if (APIUtil.isBiologicalEntity(folderItem.id)) {
              return RegistryIdentifierUtilRest.getPrimaryRegistryIdentifier(folderItem as BioentitySummary);
            }
            if (APIUtil.isBaseEntry(folderItem.id)) {
              return (folderItem as EntrySummary).humanId;
            }
            if (APIUtil.isDataset(folderItem.id)) {
              return (folderItem as DatasetListingGraphQL).displayId;
            }
            return null;
          },
          isHidden: (folderItem: ListableFolderItem) =>
            !APIUtil.isBaseEntry(folderItem.id) &&
            !APIUtil.isBiologicalEntity(folderItem.id) &&
            !APIUtil.isDataset(folderItem.id),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          accessorKey: ColumnAccessorKey.CREATED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
          width: 120,
        },
        render: (folderItem: ListableFolderItem) => {
          const createdAt =
            (folderItem as FileSummary).created_at || (folderItem as DatasetListingGraphQL).createdAt;
          return (
            <Tooltip
              title={DateUtil.readableTime(createdAt, {showTime: true})}
              container="body"
              placement={TooltipPlacement.TOP}
              delay={350}
            >
              <span className="u-ellipsis-text">{DateUtil.readableTime(createdAt, {showTime: false})}</span>
            </Tooltip>
          );
        },
      } satisfies ColumnConfiguration<ListableFolderItem>,
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
          width: 120,
        },
        render: (folderItem: ListableFolderItem) => (
          <Tooltip
            title={FileBrowserUtil.getResultDate(folderItem, {showTime: true, forceModifiedAt: true}).date}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {FileBrowserUtil.getResultDate(folderItem, {showTime: false, forceModifiedAt: true}).date}
            </span>
          </Tooltip>
        ),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      {
        column: {
          header: 'Entry Dates',
          accessorKey: ColumnAccessorKey.ENTRY_DATES,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: false,
          width: null,
        },
        render: (folderItem: ListableFolderItem) => (
          <Tooltip
            title={FileBrowserUtil.getResultDate(folderItem, {showTime: true}).date}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {FileBrowserUtil.getResultDate(folderItem, {showTime: false}).date}
            </span>
          </Tooltip>
        ),
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      {
        column: {
          header: 'Authors',
          accessorKey: ColumnAccessorKey.AUTHORS,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) => <ConnectedAuthors itemId={folderItem.id} />,
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.hasAuthors(folderItem.id),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      maybeGetStudiesListingColumn<ListableFolderItem>(itemStudyAttributes),
      {
        column: {
          header: 'Review Status',
          accessorKey: ColumnAccessorKey.STATUS,
          flexGrow: 1,
          isCollapsible: true,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) => (
          <ReviewStatus review={(folderItem as EntrySummary).review} />
        ),
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      licenses.hasLicense('LICENSE_REVIEWS') && {
        column: {
          header: 'Review initially started',
          accessorKey: ColumnAccessorKey.REVIEW_TIMESTAMP_FIRST_STARTED,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) =>
          APIUtil.isBaseEntry(folderItem.id) && (
            <DatetimeTableCell
              datetime={
                reviewChangeTimestampsByEntryId && reviewChangeTimestampsByEntryId[folderItem.id]
                  ? reviewChangeTimestampsByEntryId[folderItem.id]!.reviewFirstStartedOn
                  : null
              }
              showTimeInCell={false}
            />
          ),
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
      },
      licenses.hasLicense('LICENSE_REVIEWS') && {
        column: {
          header: 'Review last started',
          accessorKey: ColumnAccessorKey.REVIEW_TIMESTAMP_LAST_STARTED,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) =>
          APIUtil.isBaseEntry(folderItem.id) && (
            <DatetimeTableCell
              datetime={
                reviewChangeTimestampsByEntryId && reviewChangeTimestampsByEntryId[folderItem.id]
                  ? reviewChangeTimestampsByEntryId[folderItem.id]!.reviewLastStartedOn
                  : null
              }
              showTimeInCell={false}
            />
          ),
        isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
      },
      licenses.hasLicense('LICENSE_REVIEWS') &&
        ({
          column: {
            header: 'Review completed',
            accessorKey: ColumnAccessorKey.REVIEW_TIMESTAMP_LAST_ACCEPTED,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          render: (folderItem: ListableFolderItem) =>
            APIUtil.isBaseEntry(folderItem.id) && (
              <DatetimeTableCell
                datetime={
                  reviewChangeTimestampsByEntryId && reviewChangeTimestampsByEntryId[folderItem.id] != null
                    ? reviewChangeTimestampsByEntryId[folderItem.id]!.reviewLastAcceptedOn
                    : null
                }
                showTimeInCell={false}
              />
            ),
          isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBaseEntry(folderItem.id),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      ...(licenses.hasLicense('LICENSE_REVIEWS') && BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES
        ? getReviewLifecycleColumns()
        : []),
      (isNotebookSection || isAllSection) &&
        ({
          column: {
            header: 'Description',
            accessorKey: ColumnAccessorKey.DESCRIPTION,
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          },
          // TODO(TypeScript): folderItem isn't actually always a folder. Come up with a cleaner way to only
          // show description if relevant.
          render: (folderItem: ListableFolderItem) => (folderItem as StringIDFolder).description,
        } satisfies ColumnConfiguration<ListableFolderItem>),
      {
        column: {
          header: 'Sequence',
          accessorKey: ColumnAccessorKey.RESIDUES,
          flexGrow: 1,
          icon: 'protein',
          isCollapsible: true,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) => (
          <span className="selectable mono">{FileUtil.getResidues(folderItem as BioentitySummary)}</span>
        ),
        isHidden: (folderItem: ListableFolderItem) =>
          !('tagSchema' in folderItem && folderItem.tagSchema && folderItem.tagSchema.showResidues),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      {
        column: {
          header: 'GC %',
          accessorKey: ColumnAccessorKey.GC_PERCENTAGE,
          isCollapsible: false,
          isSortable: false,
        },
        isHidden: (folderItem: ListableFolderItem) => !FileUtil.isOligo(folderItem as SequenceSummary),
        render: (folderItem: ListableFolderItem) =>
          `${(
            100 *
            biojsUtil.gc(
              (folderItem as SequenceSummary).bases!,
              (folderItem as SequenceSummary).nucleotideType
            )
          ).toFixed(2)}%`,
      } satisfies ColumnConfiguration<ListableFolderItem>,
      {
        column: {
          header: 'Tm',
          accessorKey: ColumnAccessorKey.MELTING_TEMPERATURE,
          isCollapsible: false,
          isSortable: false,
        },
        render: (folderItem: ListableFolderItem) =>
          `${biojsUtil
            .meltingTemperature(
              (folderItem as SequenceSummary).bases!,
              (folderItem as SequenceSummary).nucleotideType
            )
            .toFixed(2)}\u00B0C`,
        isHidden: (folderItem: ListableFolderItem) => !FileUtil.isOligo(folderItem as SequenceSummary),
      } satisfies ColumnConfiguration<ListableFolderItem>,
      isRegistry &&
        ({
          column: ExpandedTableTagUtil.validationErrorsColumn,
          render: (folderItem: ListableFolderItem) => {
            if (!validationExtrasByBioentityId) {
              return null;
            }
            const validationExtras = validationExtrasByBioentityId[folderItem.id];
            if (!validationExtras) {
              return 'Loading...';
            }
            let error = getValidationErrorText(
              (folderItem as BioentitySummary).schemaValidationState || null,
              validationExtras,
              true
            );
            if (validationExtras.errorRecords && validationExtras.errorRecords.length > 1) {
              error += ` and ${validationExtras.errorRecords.length - 1} more`;
            }
            return error;
          },
          isHidden: (folderItem: ListableFolderItem) => !APIUtil.isBiologicalEntity(folderItem.id),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      shouldShowMixturesColumn &&
        ({
          column: {
            header: 'Amount',
            accessorKey: ColumnAccessorKey.MIXTURE_AMOUNT,
            width: 100,
          },
          render: (folderItem: ListableFolderItem) => {
            if (!mixturesById) {
              return null;
            }
            const mixture = mixturesById[folderItem.id];
            if (!(mixture?.amount && mixture?.units)) {
              return null;
            }
            return `${mixture.amount}${mixture.units}`;
          },
          isHidden: (folderItem: ListableFolderItem) => !APIUtil.isMixture(folderItem.id),
        } satisfies ColumnConfiguration<ListableFolderItem>),
      shouldShowMixturesColumn &&
        ({
          column: {
            header: 'Ingredients',
            accessorKey: ColumnAccessorKey.MIXTURE_INGREDIENTS,
            width: 200,
          },
          render: (folderItem: ListableFolderItem) => {
            if (!mixturesById) {
              return null;
            }
            return <MixtureIngredientsList mixture={mixturesById[folderItem.id]} />;
          },
          isHidden: (folderItem: ListableFolderItem) => !APIUtil.isMixture(folderItem.id),
        } satisfies ColumnConfiguration<ListableFolderItem>),
    ]);
    maybeAddChemicalStructureColumn(columns, folderItems, chemSearchFolderItemListingExtras);
    maybeAddChemicalSimilarityColumn(columns, folderItems, chemSearchFolderItemListingExtras);

    const tagColumns = ExpandedTableTagUtil.listFileTagColumns(folderItems);
    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map(
          (column: ScrollableTableColumn) =>
            ({
              column,
              render: (folderItem: ListableFolderItem) => {
                const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
                  tags: ExpandedTableTagUtil.getFolderItemTags(folderItem),
                  tagColumns: [column],
                });
                return tagsByColumn.tagFields[column.accessorKey];
              },
            }) satisfies ColumnConfiguration<ListableFolderItem>
        )
      );
    }
    maybeAddWorklistPositionColumn(columns, folderItems);

    const starColumn = {
      column: {
        header: getColumnHeaderFromSortKey(SortKey.SORT_STARRED),
        accessorKey: ColumnAccessorKey.STAR,
        isCollapsible: false,
        isSortable: true,
        sortKey: SortKey.SORT_STARRED,
        width: 70,
      },
      render: (item: FileBrowserConstants.FolderItem) =>
        starredItemsByObjectId &&
        isCoreObjectWithInterface(item, DomainGraphInterface.STARRABLE) && (
          <CoreObjectStarIconRenderer item={item} isStarred={starredItemsByObjectId[item.id] || false} />
        ),
      isHidden: (item: FileBrowserConstants.FolderItem) =>
        !starredItemsByObjectId || !isCoreObjectWithInterface(item, DomainGraphInterface.STARRABLE),
    };
    columns.splice(1, 0, starColumn);

    return columns;
  }

  getNonPluginRowContentHeight(
    folderItem: ListableFolderItem,
    {mixturesById, itemStudyAttributes}: FolderItemListingExtras
  ): number {
    const numMixtureIngredients =
      mixturesById && mixturesById[folderItem.id] ? mixturesById[folderItem.id].ingredients.length : 0;
    return Math.max(
      ExpandedTableTagUtil.getRowHeight(ExpandedTableTagUtil.getFolderItemTags(folderItem)),
      getChipsRowHeight(numMixtureIngredients, ChipSize.SMALL),
      APIUtil.isMolecule(folderItem.id) ? MOLECULE_ROW_HEIGHT : 0,
      getStudiesRowContentHeight(folderItem.id, itemStudyAttributes)
    );
  }
}

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
  },
});
